import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Avatar,
  Chip,
  Fab,
  Slide,
  Fade,
  useTheme,
  useMediaQuery,
  alpha,
  Badge,
  Tooltip,
  Divider,
  Button,
  Rating,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  SmartToy as AIIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Chat as ChatIcon,
  Circle as CircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useCurrency } from './CurrencyContext';
import { useCart } from './CartContext';
import {
  createConversation,
  sendMessage,
  rateConversation,
  formatChatTimestamp,
  isValidMessage,
  cleanSuggestionText,
  getErrorMessage
} from './aiChatApi';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const typing = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-10px); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-20px); }
  60% { transform: translateY(-10px); }
`;

const MessageBubble = ({ message, isUser, timestamp }) => {
  const theme = useTheme();

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
          animation: `${fadeIn} 0.3s ease-out`,
          px: { xs: 2, sm: 3 }
        }}
      >
        {!isUser && (
          <Avatar
            sx={{
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              bgcolor: 'primary.main',
              mr: 1,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <AIIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
          </Avatar>
        )}

        <Box sx={{ maxWidth: { xs: '75%', sm: '70%', md: '65%' } }}>
          <Box
            sx={{
              bgcolor: isUser ? 'primary.main' : alpha(theme.palette.grey[200], 0.9),
              color: isUser ? 'white' : 'text.primary',
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 2 },
              borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, isUser ? 0.2 : 0.08)}`,
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.6,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                [isUser ? 'right' : 'left']: -8,
                bottom: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: isUser ? '0 0 16px 16px' : '0 16px 16px 0',
                borderColor: isUser
                  ? `transparent transparent ${theme.palette.primary.main} transparent`
                  : `transparent ${alpha(theme.palette.grey[200], 0.9)} transparent transparent`
              }
            }}
          >
            {message}
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              px: 1,
              color: 'text.secondary',
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              textAlign: isUser ? 'right' : 'left'
            }}
          >
            {formatChatTimestamp(timestamp)}
          </Typography>
        </Box>

        {isUser && (
          <Avatar
            sx={{
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              bgcolor: 'secondary.main',
              ml: 1,
              fontSize: { xs: '1rem', sm: '1.2rem' }
            }}
          >
            U
          </Avatar>
        )}
      </Box>
    </Fade>
  );
};

const TypingIndicator = () => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 2, sm: 3 }, py: 2 }}>
      <Avatar
        sx={{
          width: { xs: 32, sm: 40 },
          height: { xs: 32, sm: 40 },
          bgcolor: 'primary.main',
          mr: 1
        }}
      >
        <AIIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
      </Avatar>

      <Box
        sx={{
          bgcolor: alpha(theme.palette.grey[200], 0.9),
          px: 3,
          py: 2,
          borderRadius: '20px 20px 20px 4px',
          display: 'flex',
          gap: 0.5
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'grey.500',
              animation: `${typing} 1.4s infinite`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const SuggestionChips = ({ suggestions, onSuggestionClick, disabled }) => {
  const theme = useTheme();

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          px: { xs: 2, sm: 3 },
          mb: 2
        }}
      >
        {suggestions.map((suggestion, idx) => (
          <Chip
            key={idx}
            label={suggestion}
            size="small"
            onClick={() => !disabled && onSuggestionClick(suggestion)}
            disabled={disabled}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              '&:hover': disabled ? {} : {
                bgcolor: 'primary.main',
                color: 'white',
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
              },
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </Box>
    </Fade>
  );
};

const AIChat = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { selectedCurrency } = useCurrency();
  const { selectedBranch } = useCart();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [isEscalated, setIsEscalated] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, suggestions]);

  useEffect(() => {
    if (!open) return;

    const initChat = async () => {
      setIsInitializing(true);
      setError(null);

      try {
        let user = null;
        try {
          user = JSON.parse(localStorage.getItem('user'));
        } catch (e) {
          console.warn('⚠️ No user in localStorage');
        }

        const branchId = selectedBranch?.id || selectedBranch?.Id || null;

        console.log('🚀 Initializing chat with:', {
          userId: user?.Id || 'guest',
          branchId: branchId,
          language: i18n.language,
          currency: selectedCurrency
        });

        const conversation = await createConversation({
          userId: user?.Id || null,
          branchId: branchId,
          language: i18n.language,
          currency: selectedCurrency
        });

        console.log('✅ Conversation created:', conversation.conversationId);

        setConversationId(conversation.conversationId);
        setMessages([
          {
            role: 'assistant',
            content: conversation.message,
            timestamp: new Date()
          }
        ]);

        if (conversation.suggestions && conversation.suggestions.length > 0) {
          setSuggestions(conversation.suggestions);
          console.log('💡 Initial suggestions:', conversation.suggestions);
        }

        setIsInitializing(false);

      } catch (error) {
        console.error('❌ Failed to initialize chat:', error);
        setError(getErrorMessage(error, i18n.language));
        setShowError(true);
        setIsInitializing(false);
      }
    };

    initChat();
  }, [open, selectedBranch, selectedCurrency, i18n.language]);

  const handleSend = async () => {
    if (!isValidMessage(input)) {
      console.warn('⚠️ Invalid message');
      return;
    }

    if (!conversationId) {
      console.warn('⚠️ No conversation ID');
      setError(i18n.language === 'ar' ? 'لم يتم إنشاء المحادثة' : 'Conversation not created');
      setShowError(true);
      return;
    }

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    console.log('📤 Sending message via HTTP:', input.substring(0, 50) + '...');

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setSuggestions([]);

    try {
      const response = await sendMessage(conversationId, input.trim(), selectedCurrency);

      console.log('✅ AI Response received:', response);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: response.message,
          timestamp: new Date()
        }
      ]);

      if (response.suggestions && response.suggestions.length > 0) {
        setSuggestions(response.suggestions);
      }

      if (response.escalated) {
        setIsEscalated(true);
      }

      setIsTyping(false);

    } catch (error) {
      console.error('❌ Send message error:', error);
      setError(getErrorMessage(error, i18n.language));
      setShowError(true);
      setIsTyping(false);
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (isTyping) {
      console.warn('⚠️ Cannot use suggestion - AI is typing');
      return;
    }

    console.log('💡 Suggestion clicked:', suggestion);
    const cleanText = cleanSuggestionText(suggestion);
    setInput(cleanText);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRateConversation = async (value) => {
    setRating(value);

    if (conversationId && value > 0) {
      try {
        await rateConversation(conversationId, value);
        console.log('⭐ Conversation rated:', value);
        setShowRating(false);

        setMessages((prev) => [
          ...prev,
          {
            role: 'system',
            content: i18n.language === 'ar'
              ? '🙏 شكراً لتقييمك!'
              : '🙏 Thank you for your feedback!',
            timestamp: new Date()
          }
        ]);
      } catch (error) {
        console.error('❌ Failed to rate conversation:', error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);

    if (messages.length > 5 && !isEscalated && !rating) {
      setTimeout(() => setShowRating(true), 500);
    }
  };

  useEffect(() => {
    if (open) {
      setUnreadCount(0);
    }
  }, [open]);

  return (
    <>
      {!open && (
        <Tooltip title={t('aiChat.askMe', 'Ask me anything!')} placement="left" arrow>
          <Fab
            color="primary"
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed',
              right: { xs: 16, sm: 24, md: 32 },
              bottom: { xs: 80, sm: 100, md: 130 },
              zIndex: 1100,
              width: { xs: 56, sm: 64, md: 72 },
              height: { xs: 56, sm: 64, md: 72 },
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
              animation: unreadCount > 0 ? `${bounce} 1s ease infinite` : `${pulse} 2s ease-in-out infinite`,
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  minWidth: { xs: 18, sm: 20 },
                  height: { xs: 18, sm: 20 }
                }
              }}
            >
              <ChatIcon sx={{ fontSize: { xs: 28, sm: 32, md: 36 } }} />
            </Badge>
          </Fab>
        </Tooltip>
      )}

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{ zIndex: 1200 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '100vw',
            mt: { xs: '86px', sm: '106px', md: '126px' },
            height: { xs: 'calc(100vh - 86px)', sm: 'calc(100vh - 106px)', md: 'calc(100vh - 126px)' },
            borderTopLeftRadius: { xs: 16, sm: 20 },
            borderBottomLeftRadius: { xs: 0, sm: 0 },
            overflow: 'hidden'
          }
        }}
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
          BackdropProps: {
            sx: { backgroundColor: 'transparent' }
          }
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            p: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                bgcolor: 'white',
                color: 'primary.main',
                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`
              }}
            >
              <AIIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
            </Avatar>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                {t('aiChat.title', 'Storesye AI')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleIcon
                  sx={{
                    fontSize: 10,
                    color: '#4caf50',
                    animation: `${pulse} 2s ease-in-out infinite`
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {t('aiChat.online', 'Online')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.1),
                transform: 'rotate(90deg)'
              },
              transition: 'transform 0.3s ease'
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {isEscalated && (
          <Slide direction="down" in={isEscalated}>
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{
                borderRadius: 0,
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.8rem', sm: '0.85rem' }
                }
              }}
            >
              {t('aiChat.escalated', 'Human support will respond shortly')}
            </Alert>
          </Slide>
        )}

        {isInitializing && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              {t('aiChat.initializing', 'Initializing chat...')}
            </Typography>
          </Box>
        )}

        {!isInitializing && (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: { xs: 1, sm: 2 },
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              backgroundImage: `
                radial-gradient(circle at 20px 20px, ${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 0),
                radial-gradient(circle at 80px 80px, ${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 0)
              `,
              backgroundSize: '100px 100px'
            }}
          >
            {messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                message={msg.content}
                isUser={msg.role === 'user'}
                timestamp={msg.timestamp}
              />
            ))}

            {isTyping && <TypingIndicator />}

            {!isTyping && (
              <SuggestionChips
                suggestions={suggestions}
                onSuggestionClick={handleSuggestionClick}
                disabled={isTyping}
              />
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}

        <Divider />

        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            bgcolor: 'white',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 -4px 20px ${alpha(theme.palette.common.black, 0.05)}`
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('aiChat.placeholder', 'Type your message...')}
              disabled={isTyping || isInitializing}
              inputRef={inputRef}
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.grey[100], 0.8)
                  },
                  '&.Mui-focused': {
                    bgcolor: 'white'
                  }
                }
              }}
            />

            <IconButton
              onClick={handleSend}
              disabled={!isValidMessage(input) || isTyping || isInitializing}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: { xs: 44, sm: 48 },
                height: { xs: 44, sm: 48 },
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.1)'
                },
                '&:disabled': {
                  bgcolor: 'grey.300'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <SendIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {[
              { label: '🔍 ' + t('aiChat.search', 'Search'), value: i18n.language === 'ar' ? 'ابحث عن منتجات' : 'search for products' },
              { label: '📦 ' + t('aiChat.track', 'Track'), value: i18n.language === 'ar' ? 'تتبع طلبي' : 'track my order' },
              { label: '❓ ' + t('aiChat.help', 'Help'), value: i18n.language === 'ar' ? 'مساعدة' : 'help' }
            ].map((quick, idx) => (
              <Chip
                key={idx}
                label={quick.label}
                size="small"
                onClick={() => {
                  if (!isTyping) {
                    setInput(quick.value);
                    inputRef.current?.focus();
                  }
                }}
                disabled={isTyping}
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 26, sm: 30 },
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5
                  },
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1.5,
              color: 'text.secondary',
              fontSize: { xs: '0.65rem', sm: '0.7rem' }
            }}
          >
            {t('aiChat.poweredBy', 'Powered by Storesye AI')}
          </Typography>
        </Box>

        {showRating && !isEscalated && (
          <Slide direction="up" in={showRating}>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'white',
                p: 3,
                boxShadow: `0 -4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                zIndex: 20
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 700 }}>
                {t('aiChat.rateExperience', 'Rate Your Experience')}
              </Typography>

              <Rating
                value={rating}
                onChange={(_, value) => handleRateConversation(value)}
                size="large"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2,
                  '& .MuiRating-iconFilled': {
                    color: 'warning.main'
                  }
                }}
              />

              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowRating(false)}
              >
                {t('common.skip', 'Skip')}
              </Button>
            </Box>
          </Slide>
        )}
      </Drawer>
    </>
  );
};

export default AIChat;